import confetti from 'canvas-confetti';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import SvgCheck from '@sats-group/icons/16/check';
import Copy from '@sats-group/icons/16/copy';
import Dot from '@sats-group/icons/16/dot';
import Female from '@sats-group/icons/16/female';
import Male from '@sats-group/icons/16/male';
import Sync from '@sats-group/icons/24/sync';
import Button from '@sats-group/ui-lib/react/button/button';
import LinkButton from '@sats-group/ui-lib/react/link-button/link-button';
import Message from '@sats-group/ui-lib/react/message/message';
import Modal from '@sats-group/ui-lib/react/modal/modal';
import Tag from '@sats-group/ui-lib/react/tag/tag';
import Text from '@sats-group/ui-lib/react/text/text';

import ArrowButton from 'shared/components/arrow-button/arrow-button';
import ContentContainer from 'shared/components/content-container/content-container';
import Logo from 'shared/components/logo/logo';
import QrCode from 'shared/components/qr-code/qr-code';
import { useMessage } from 'shared/hooks/use-message';
import type { NamedFC } from 'shared/types/named-fc.types';

import { AppStoreLogo } from 'ui/components/app-store-logo/app-store-logo';
import { GooglePlayStoreLogo } from 'ui/components/google-play-store-logo/google-play-store-logo';

import type { Confirmation as Props } from './confirmation.props';
import { tagIconNames } from './confirmation.props';

const options: Record<NonNullable<Props['mood']>, confetti.Options[]> = {
  celebratory: [
    {
      origin: { y: 0.7 },
      particleCount: 100,
      spread: 26,
      startVelocity: 55,
    },
    {
      origin: { y: 0.7 },
      particleCount: 80,
      spread: 60,
    },
    {
      decay: 0.91,
      origin: { y: 0.7 },
      particleCount: 140,
      scalar: 0.8,
      spread: 100,
    },
    {
      decay: 0.92,
      origin: { y: 0.7 },
      particleCount: 40,
      scalar: 1.2,
      spread: 120,
      startVelocity: 25,
    },
    {
      origin: { y: 0.7 },
      particleCount: 40,
      spread: 120,
      startVelocity: 45,
    },
    { disableForReducedMotion: true },
  ],
};

const FlipButton: React.FunctionComponent = () => {
  return (
    <div className="confirmation-page__flip-button">
      <Sync />
    </div>
  );
};

const Confirmation: NamedFC<Props> = ({
  additionalInformation,
  emailDescription,
  hasDaysUntilMembershipStarts,
  language,
  logo,
  membershipDetails,
  messages,
  mood,
  nextSteps,
  optimizelyTrackingEvent,
  startDateDescription,
  subTitle,
  summaryCard,
  title,
}) => {
  const animationCanvas = useRef(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const [isHandheldDevice, setIsHandheldDevice] = useState<boolean | null>(
    null
  );
  const localConfetti = useRef<ReturnType<(typeof confetti)['create']>>(null);
  const { messagesQueue, getAction } = useMessage(messages, language);
  const isElixia = language === 'fi';

  useEffect(() => {
    const isHandheldDevice =
      /iPad|iPhone|Android/.test(window.navigator.userAgent) ||
      (navigator.userAgent.includes('Macintosh') &&
        navigator.maxTouchPoints > 1);

    if (isHandheldDevice) {
      setIsHandheldDevice(true);
    }
  }, []);

  useEffect(() => {
    if (!animationCanvas.current) {
      return;
    }

    if (!mood) {
      return;
    }

    if (!localConfetti.current) {
      // NOTE: The type is wrong.
      // `.current` is not read-only.
      //
      // > This value is intentionally mutable,
      // > meaning you can both read and write to it.
      // > It’s like a secret pocket of your component that React doesn’t track.
      //
      // - https://react.dev/learn/referencing-values-with-refs
      //
      localConfetti.current = confetti.create(animationCanvas.current, {
        disableForReducedMotion: true,
        resize: true,
        useWorker: true,
      });
    }

    options[mood].forEach(option => {
      if (localConfetti.current) {
        localConfetti.current(option);
      }
    });

    return () => {
      if (localConfetti.current) {
        localConfetti.current.reset();
      }
    };
  }, []);

  useEffect(() => {
    if (optimizelyTrackingEvent) {
      window.optimizely ??= [] as unknown as OptimizelyClient;

      window['optimizely'].push(optimizelyTrackingEvent);
    }
  }, [optimizelyTrackingEvent]);

  return (
    <div className="confirmation-page">
      <ContentContainer
        className="confirmation-page__summary-container"
        theme={ContentContainer.themes.medium}
      >
        <div className="confirmation-page__logo">
          <Logo {...logo} />
        </div>
        {messagesQueue.length ? (
          <div className="confirmation-page__messages">
            {messagesQueue.map(message => (
              <Message
                {...message}
                key={message.text}
                action={getAction(message)}
              />
            ))}
          </div>
        ) : null}
        <div className="confirmation-page__title">
          <Text
            theme={Text.themes.headline}
            elementName="h1"
            size={Text.sizes.headline1}
            italic
          >
            {title}
          </Text>
          <Text
            theme={Text.themes.headline}
            elementName="h2"
            size={Text.sizes.headline1}
            italic
          >
            {subTitle}
          </Text>
        </div>
        <Text>{startDateDescription}</Text>
        <div
          className={cn('confirmation-page__summary-card', {
            'confirmation-page__summary-card--flipped': isFlipped,
          })}
        >
          <div
            className={cn('confirmation-page__summary-card-front', {
              'chonfirmation-page__summary-card-front--elixia': isElixia,
            })}
            onClick={() => setIsFlipped(summaryCard.back ? true : false)}
          >
            <div className="confirmation-page__summary-card-front-top">
              <div>
                <Text
                  size={Text.sizes.small}
                  theme={Text.themes.emphasis}
                  className="confirmation-page__summary-card-front-member"
                  data-hj-mask
                >
                  {summaryCard.front.member}
                </Text>
                <Text
                  theme={Text.themes.headline}
                  size={Text.sizes.headline2}
                  italic
                >
                  {summaryCard.front.membershipName}
                </Text>
              </div>
              <div
                className={cn(
                  'confirmation-page__summary-card-front-activation-tag',
                  {
                    'confirmation-page__summary-card-front-activation-tag--inactive':
                      hasDaysUntilMembershipStarts,
                  }
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <circle
                    opacity="0.25"
                    cx="9.00073"
                    cy="9.00073"
                    r="7.56909"
                    fill={hasDaysUntilMembershipStarts ? '#7DADD0' : '#A3F0BF'}
                  />
                  <circle
                    cx="9"
                    cy="9"
                    r="4"
                    fill={hasDaysUntilMembershipStarts ? '#7DADD0' : '#A3F0BF'}
                  />
                </svg>
                <Text size={Text.sizes.interface} theme={Text.themes.emphasis}>
                  {summaryCard.front.activationTag}
                </Text>
              </div>
            </div>
            <div>
              {summaryCard.front.membershipDescription ? (
                <Text
                  size={Text.sizes.small}
                  className="confirmation-page__summary-card-front-membership-description"
                >
                  {summaryCard.front.membershipDescription}
                </Text>
              ) : null}
            </div>
            {summaryCard.front.groupTrainingIncluded || summaryCard.back ? (
              <div className="confirmation-page__summary-card-front-bottom">
                {summaryCard.front.groupTrainingIncluded ? (
                  <div className="confirmation-page__summary-card-front-bottom-gx-included">
                    <Text theme={Text.themes.headline} italic>
                      {summaryCard.front.groupTrainingIncluded.groupTraining}
                    </Text>
                    <Text theme={Text.themes.headline} italic>
                      {summaryCard.front.groupTrainingIncluded.included}
                    </Text>
                  </div>
                ) : null}
                {summaryCard.back ? <FlipButton /> : null}
              </div>
            ) : null}
          </div>
          {summaryCard.back ? (
            <div
              className={cn('confirmation-page__summary-card-back', {
                'confirmation-page__summary-card-back--elixia': isElixia,
              })}
              onClick={() => setIsFlipped(false)}
            >
              <Logo language={language} />
              {summaryCard.back.addons.length ? (
                <div className="confirmation-page__summary-back-addons-section">
                  <Text theme={Text.themes.emphasis} size={Text.sizes.small}>
                    {summaryCard.back.addonsTitle}
                  </Text>
                  <div className="confirmation-page__summary-back-addons">
                    {summaryCard.back.addons.map(addon => (
                      <Tag
                        key={addon.text}
                        theme={Tag.themes.fixed}
                        variant={Tag.variants.secondary}
                        size={Tag.sizes.xs}
                        {...addon}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="confirmation-page__summary-card-back-bottom">
                {summaryCard.back.otherPayer ? (
                  <div className="confirmation-page__summary-card-back-bottom-other-payer-section">
                    <Text size={Text.sizes.interface}>
                      {summaryCard.back.otherPayerTitle}
                    </Text>
                    <Tag
                      theme={Tag.themes.fixed}
                      variant={Tag.variants.secondary}
                      size={Tag.sizes.xs}
                      {...summaryCard.back.otherPayer}
                      data-hj-mask
                    />
                  </div>
                ) : null}
                <FlipButton />
              </div>
            </div>
          ) : null}
        </div>
        <div className="confirmation-page__email-description">
          {emailDescription
            .split(/([^\s@]+@[^\s@]+\.[^\s@]+)/)
            .map((part, index) =>
              /[^\s@]+@[^\s@]+\.[^\s@]+/.test(part) ? (
                <Text key={`${part}-${index}`} theme={Text.themes.emphasis}>
                  {part}
                </Text>
              ) : (
                <Text key={`${part}-${index}`}>{part}</Text>
              )
            )}
        </div>
        <ArrowButton
          {...membershipDetails.button}
          popoverTarget={membershipDetails.modal.id}
        />
        <Modal
          background={Modal.backgrounds.secondary}
          {...membershipDetails.modal}
        >
          <div className="confirmation-page__membership-details">
            {membershipDetails.sections.map(section => (
              <div
                key={section.title}
                className="confirmation-page__membership-details-section"
              >
                <Text theme={Text.themes.emphasis}>{section.title}</Text>
                <div className="confirmation-page__membership-details-items">
                  {section.items.map(item => (
                    <div
                      key={item.label}
                      className="confirmation-page__membership-details-item"
                    >
                      <div className="confirmation-page__membership-details-item-values">
                        <Text size={Text.sizes.small}>{item.label}</Text>
                        <div
                          className="confirmation-page__membership-details-item-value"
                          data-hj-mask
                        >
                          <Text>{item.value}</Text>
                          {item.copyButton ? (
                            <Button
                              {...item.copyButton}
                              iconOnly
                              leadingIcon={
                                copiedLabel === item.label ? (
                                  <SvgCheck />
                                ) : (
                                  <Copy />
                                )
                              }
                              size={Button.sizes.small}
                              variant={Button.variants.tertiary}
                              className="confirmation-page__membership-details-copy-button"
                              onClick={() =>
                                navigator.clipboard
                                  .writeText(item.value)
                                  .then(() => {
                                    setCopiedLabel(item.label);
                                    setTimeout(
                                      () => setCopiedLabel(null),
                                      2000
                                    );
                                  })
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                      {item.tag ? (
                        <Tag
                          {...item.tag}
                          size={Tag.sizes.s}
                          icon={
                            item.tag.iconName === tagIconNames.male ? (
                              <Male />
                            ) : item.tag.iconName === tagIconNames.female ? (
                              <Female />
                            ) : item.tag.iconName === tagIconNames.dot ? (
                              <Dot />
                            ) : undefined
                          }
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      </ContentContainer>
      <div className="confirmation-page__next-steps-container">
        <ContentContainer theme={ContentContainer.themes.medium}>
          <div className="confirmation-page__next-steps">
            <Text size={Text.sizes.headline3} theme={Text.themes.emphasis}>
              {nextSteps.title}
            </Text>
            <div className="confirmation-page__steps">
              {nextSteps.appStep ? (
                <div
                  key={nextSteps.appStep.tag.text}
                  className="confirmation-page__step"
                >
                  <div className="confirmation-page__tag">
                    <Tag
                      size={Tag.sizes.s}
                      variant={Tag.variants.tertiary}
                      {...nextSteps.appStep.tag}
                    />
                  </div>
                  <div className="confirmation-page__descriptions">
                    <Text size={Text.sizes.small}>
                      {nextSteps.appStep.preTitle}
                    </Text>
                    <Text
                      size={Text.sizes.headline3}
                      theme={Text.themes.emphasis}
                    >
                      {nextSteps.appStep.title}
                    </Text>
                  </div>
                  <div className="confirmation-page__actions">
                    {isHandheldDevice ? (
                      <div className="confirmation-page__app-links">
                        <a
                          aria-label={nextSteps.appStep.googlePlayLabel}
                          href={nextSteps.appStep.googlePlayHref}
                        >
                          {<GooglePlayStoreLogo />}
                        </a>
                        <a
                          aria-label={nextSteps.appStep.appStoreLabel}
                          href={nextSteps.appStep.appStoreHref}
                        >
                          {<AppStoreLogo />}
                        </a>
                      </div>
                    ) : (
                      <Button
                        popoverTarget={nextSteps.appStep.modal.id}
                        wide
                        {...nextSteps.appStep.button}
                      />
                    )}
                  </div>
                  <div className="confirmation-page__benefits">
                    {nextSteps.appStep.benefits.map(benefit => (
                      <div key={benefit} className="confirmation-page__benefit">
                        <SvgCheck />
                        <Text>{benefit}</Text>
                      </div>
                    ))}
                  </div>
                  {!isHandheldDevice ? (
                    <Modal
                      background={Modal.backgrounds.secondary}
                      {...nextSteps.appStep.modal}
                    >
                      {nextSteps.appStep.qrCode ? (
                        <div className="confirmation-page__modal-children">
                          <QrCode {...nextSteps.appStep.qrCode} />
                          <Text
                            className="confirmation-page__qr-code-help-text"
                            size={Text.sizes.small}
                          >
                            {nextSteps.appStep.qrCodeHelpText}
                          </Text>
                        </div>
                      ) : null}
                    </Modal>
                  ) : null}
                </div>
              ) : null}
              {nextSteps.hubStep ? (
                <div
                  key={nextSteps.hubStep.tag.text}
                  className="confirmation-page__step"
                >
                  <div className="confirmation-page__tag">
                    <Tag
                      size={Tag.sizes.s}
                      variant={Tag.variants.tertiary}
                      {...nextSteps.hubStep.tag}
                    />
                  </div>
                  <div className="confirmation-page__descriptions">
                    <Text size={Text.sizes.small}>
                      {nextSteps.hubStep.preTitle}
                    </Text>
                    <Text
                      size={Text.sizes.headline3}
                      theme={Text.themes.emphasis}
                    >
                      {nextSteps.hubStep.title}
                    </Text>
                  </div>
                  <LinkButton wide {...nextSteps.hubStep.link} />
                  <div className="confirmation-page__benefits">
                    {nextSteps.hubStep.benefits.map(benefit => (
                      <div key={benefit} className="confirmation-page__benefit">
                        <SvgCheck />
                        <Text>{benefit}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {nextSteps.ptTrialStep ? (
                <div
                  key={nextSteps.ptTrialStep.tag.text}
                  className="confirmation-page__step"
                >
                  <div className="confirmation-page__tag">
                    <Tag
                      size={Tag.sizes.s}
                      variant={Tag.variants.tertiary}
                      {...nextSteps.ptTrialStep.tag}
                    />
                  </div>
                  <div className="confirmation-page__descriptions">
                    <Text size={Text.sizes.small}>
                      {nextSteps.ptTrialStep.preTitle}
                    </Text>
                    <Text
                      size={Text.sizes.headline3}
                      theme={Text.themes.emphasis}
                    >
                      {nextSteps.ptTrialStep.title}
                    </Text>
                  </div>
                  {isHandheldDevice ? (
                    <LinkButton
                      wide
                      href={nextSteps.ptTrialStep.handheldHref}
                      text={nextSteps.ptTrialStep.button.text}
                    />
                  ) : (
                    <Button
                      popoverTarget={nextSteps.ptTrialStep.modal.id}
                      wide
                      {...nextSteps.ptTrialStep.button}
                    />
                  )}
                  <div className="confirmation-page__benefits">
                    {nextSteps.ptTrialStep.benefits.map(benefit => (
                      <div key={benefit} className="confirmation-page__benefit">
                        <SvgCheck />
                        <Text>{benefit}</Text>
                      </div>
                    ))}
                  </div>
                  {!isHandheldDevice && nextSteps.ptTrialStep.modal ? (
                    <Modal
                      background={Modal.backgrounds.secondary}
                      {...nextSteps.ptTrialStep.modal}
                    >
                      {nextSteps.ptTrialStep.qrCode ? (
                        <div className="confirmation-page__modal-children">
                          <QrCode {...nextSteps.ptTrialStep.qrCode} />
                          <Text
                            className="confirmation-page__qr-code-help-text"
                            size={Text.sizes.small}
                          >
                            {nextSteps.ptTrialStep.qrCodeHelpText}
                          </Text>
                        </div>
                      ) : null}
                    </Modal>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </ContentContainer>
      </div>
      <ContentContainer theme={ContentContainer.themes.medium}>
        <div className="confirmation-page__additional-information">
          <Text size={Text.sizes.headline3} theme={Text.themes.emphasis}>
            {additionalInformation.title}
          </Text>
          <div className="confirmation-page__additional-information-blocks">
            {additionalInformation.blocks.map(block => (
              <div
                key={block.title}
                className="confirmation-page__additional-information-block"
              >
                <Text theme={Text.themes.emphasis}>{block.title}</Text>
                <Text elementName="p">{block.text}</Text>
                {block.link ? (
                  <div className="confirmation-page__additional-information-block-link">
                    <LinkButton variant="tertiary" {...block.link} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </ContentContainer>
      <aside className="confirmation-page__animation" role="presentation">
        <canvas ref={animationCanvas} />
      </aside>
    </div>
  );
};

Confirmation.displayName = 'Confirmation';

export default Confirmation;
